import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";

import { HyphenIcon, OverflowText } from "@transfr-inc/dashboard-components";
import {
  SortDirection,
  getSortMethod,
} from "@transfr-inc/dashboard-components/data-table";
import { useSortHeader } from "@transfr-inc/dashboard-components/forms";

import { formatDateSlashes } from "@src/utils/utils";
import SyncBadge from "@views/integrations/list/components/sync-badge";

const DEFAULT_SORT_HEADER = "organizationName";

export const useIntegrationsSortHeader = ({
  filteredIntegrations,
  setSyncErrors,
}) => {
  const sortHeader = useSortHeader({
    data: [
      {
        title: "Parent",
        propName: "parentOrganizationName",
        renderer: (integration) =>
          integration.parentOrganizationName ? (
            <OverflowText text={integration.parentOrganizationName}>
              {integration.parentOrganizationName}
            </OverflowText>
          ) : (
            <HyphenIcon />
          ),
      },
      {
        title: "Organization",
        propName: DEFAULT_SORT_HEADER,
        renderer: (integration) => (
          <OverflowText text={integration.organizationName}>
            {integration.organizationName}
          </OverflowText>
        ),
      },
      {
        title: "Provider",
        propName: "idpName",
        renderer: (integration) => (
          <img
            className="idp-icon"
            src={integration.idpIcon}
            alt={integration.idpName}
          />
        ),
      },
      {
        title: "SSO",
        propName: "ssoEnabled",
        renderer: (integration) =>
          integration.ssoEnabled ? (
            <FontAwesomeIcon
              className="sso-enabled"
              icon="fa-solid fa-circle-check"
            />
          ) : (
            <HyphenIcon />
          ),
      },
      {
        title: "Status",
        propName: "status",
        renderer: (integration) => (
          <SyncBadge
            connectionId={integration.connectionId}
            jobExecutionId={integration.jobExecutionId}
            status={integration.status}
            organizationCode={integration.organization.code}
            onErrorClick={setSyncErrors}
          />
        ),
      },
      {
        title: "Last Sync",
        propName: "executionDateTime",
        renderer: (integration) => {
          const executionDateTime = integration.executionDateTime;
          return (
            <div className="execution-date-time">
              {formatDateSlashes(executionDateTime)}
            </div>
          );
        },
      },
      {
        title: "Records",
        propName: "totalRecords",
        renderer: (integration) => (
          <div className="records">
            +{integration.createdRecords}, ~{integration.updatedRecords}, !
            {integration.erroredRecords}, {integration.totalRecords} total
          </div>
        ),
      },
    ],
    onSortHandler: (header, sortDirection) => {
      const sortDirectionChar =
        sortDirection === SortDirection.DESCENDING ? "-" : "+";
      const sortParam = [`${sortDirectionChar}${header.propName}`];
      let sortFunc = getSortMethod(sortParam);
      // Default to sorting by organization name
      if (sortDirection === SortDirection.DEFAULT) {
        sortHeader.headers.find(
          (header) => header.propName === DEFAULT_SORT_HEADER,
        ).sortDirection = SortDirection.ASCENDING;
        sortFunc = getSortMethod([`+${DEFAULT_SORT_HEADER}`]);
      }
      filteredIntegrations?.sort(sortFunc);
    },
  });

  useEffect(() => {
    if (sortHeader.headers?.length > 0) {
      // Make sure the default sort renders correctly on page load
      sortHeader.headers.find(
        (header) => header.propName === DEFAULT_SORT_HEADER,
      ).sortDirection = SortDirection.ASCENDING;
    }
  }, [sortHeader.headers?.length]);

  return sortHeader;
};
